import React, { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useServices } from "../../../hooks/useServices";
import { UserInfoFormStyled } from "./user-info-form.style";
import SelectFormField from "../../../components/form-fields/select-field/select-form-field";
import WorkSchedule from "../../../components/form-fields/work-schedule/work-schedule";
import { TextInputFormField } from "../../../components/form-fields/text-input-field/text-input-form-field";
import DatePickerFormField from "../../../components/form-fields/date-picker/date-picker-form-field";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  PERMISSIONS_OPTIONS,
  DEFAULT_WORK_SCHEDULE,
} from "../../../constants/constants";

export const USER_INFO_FORM_VIEW_TYPES = {
  detail: "detail",
  creation: "creation",
};

const UserInfoForm = ({ form, view = "creation" }) => {
  const { control, watch, setValue, getValues } = form;

  // Dynamic fields
  const selectedRole = watch("role");
  const selectedServiceGroup = watch("serviceGroup");
  const employmentStatus = watch("employmentStatus");

  const workSchedule = useWatch({
    control,
    name: "workSchedule",
    defaultValue: DEFAULT_WORK_SCHEDULE,
  });

  const formData = getValues();
  const isDetailedView = view === USER_INFO_FORM_VIEW_TYPES?.detail;
  const isCreationView = view === USER_INFO_FORM_VIEW_TYPES.creation;
  const isEndDateDisabled = employmentStatus !== "Inactive";

  // Fetch all services to derive rates by role
  const { data: services = [] } = useServices();

  // build a map: roleName -> rate
  const serviceRates = useMemo(() => {
    const map = {};
    services.forEach((svc) => {
      // assuming svc.roles is an array of { name, rate }
      if (Array.isArray(svc.roles)) {
        svc.roles.forEach((role) => {
          map[role.name] = role.rate;
        });
      }
    });
    return map;
  }, [services]);

  // whenever role changes, auto-populate rate
  useEffect(() => {
    if (selectedRole && serviceRates[selectedRole] !== undefined) {
      setValue("rate", String(serviceRates[selectedRole]));
    }
  }, [selectedRole, serviceRates, setValue]);

  // Build options for service groups
  const serviceGroupOptions = useMemo(
    () =>
      services
        .map((svc) => ({ label: svc.name, value: svc.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [services]
  );

  // Build role options filtered by selected service group
  const roleOptions = useMemo(() => {
    if (!selectedServiceGroup) return [];
    const svc = services.find((s) => s.name === selectedServiceGroup);
    return svc && Array.isArray(svc.roles)
      ? svc.roles.map((role) => ({ label: role.name, value: role.name }))
      : [];
  }, [services, selectedServiceGroup]);

  useEffect(() => {
    if (!Array.isArray(workSchedule)) return;

    const total = workSchedule.reduce(
      (sum, day) => sum + Number(day.hours || 0),
      0
    );
    setValue("weeklyHours", total, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [workSchedule, setValue]);

  const renderFieldsBasedOnView = () => {
    if (isCreationView) {
      return (
        <UserInfoFormStyled isDetailedView={isDetailedView}>
          <div className="creation-template">
            <div>
              <div className="flexCreate">
                <TextInputFormField
                  isRequired
                  label="First Name"
                  name="firstName"
                  control={control}
                  defaultValue={formData?.firstName}
                />
                <TextInputFormField
                  isRequired
                  label="Surname"
                  name="surname"
                  control={control}
                  defaultValue={formData?.surname}
                />
              </div>
              <div className="flexCreate">
                <TextInputFormField
                  isRequired
                  label="Email"
                  name="email"
                  control={control}
                  defaultValue={formData?.email}
                />
                <SelectFormField
                  control={control}
                  isRequired
                  name="employmentStatus"
                  label="State"
                  options={EMPLOYMENT_STATUS_OPTIONS}
                />
              </div>
              <div className="flexCreate">
                <SelectFormField
                  control={control}
                  isRequired
                  name="permissions"
                  label="Permissions"
                  options={PERMISSIONS_OPTIONS}
                />
                <DatePickerFormField
                  label="Start Date (mm/dd/yyyy)"
                  placeholder={"Start"}
                  name="startDate"
                  size={"max"}
                  control={control}
                />
              </div>

              <div className="flexCreate">
                <SelectFormField
                  control={control}
                  isRequired
                  name="serviceGroup"
                  label="Service Group"
                  options={serviceGroupOptions}
                  placeholder="Select Service Group"
                />
                <SelectFormField
                  control={control}
                  isRequired
                  name="role"
                  label="Role"
                  options={roleOptions}
                  placeholder={
                    !selectedServiceGroup
                      ? "Select Service Group first"
                      : "Select Role"
                  }
                  disabled={!selectedServiceGroup}
                />
              </div>
              <div className="flexCreate">
                <TextInputFormField
                  isRequired
                  label="Title"
                  name="title"
                  control={control}
                  defaultValue={formData?.title}
                />
                <TextInputFormField
                  // isRequired
                  label="Rate"
                  name="rate"
                  control={control}
                  disabled
                />
              </div>
              <div className="flexCreate">
                <TextInputFormField
                  isRequired
                  label="Cost"
                  name="cost"
                  control={control}
                  defaultValue={formData?.cost}
                  type="number"
                />
                <TextInputFormField
                  label="Weekly Hours"
                  name="weeklyHours"
                  control={control}
                  type="number"
                  disabled
                />
              </div>

              <WorkSchedule
                control={control}
                name="workSchedule"
                defaultValue={formData?.workSchedule}
                label="Work Schedule"
              />
            </div>
          </div>
        </UserInfoFormStyled>
      );
    }

    if (isDetailedView) {
      return (
        <UserInfoFormStyled isDetailedView={isDetailedView}>
          <div className="detail-template">
            <div className="left-content">
              <div className="flex">
                <TextInputFormField
                  isRequired
                  label="First Name"
                  name="firstName"
                  control={control}
                  disabled
                />
                <TextInputFormField
                  isRequired
                  label="Surname"
                  name="surname"
                  control={control}
                  disabled
                />
                <TextInputFormField
                  isRequired
                  label="Email"
                  name="email"
                  control={control}
                  disabled
                />
              </div>
              <div className="flex">
                <SelectFormField
                  control={control}
                  isRequired
                  name="employmentStatus"
                  label="State"
                  options={EMPLOYMENT_STATUS_OPTIONS}
                />
                <SelectFormField
                  control={control}
                  isRequired
                  name="permissions"
                  label="Permissions"
                  options={PERMISSIONS_OPTIONS}
                />
                <DatePickerFormField
                  label="Start Date (mm/dd/yyyy)"
                  placeholder={"Start"}
                  name="startDate"
                  size={"max"}
                  control={control}
                />
                <DatePickerFormField
                  label="End Date"
                  placeholder={"End"}
                  name="endDate"
                  size={"max"}
                  control={control}
                  disabled={isEndDateDisabled}
                />
              </div>
              <div className="flex">
                <SelectFormField
                  control={control}
                  isRequired
                  name="serviceGroup"
                  label="Service Group"
                  options={serviceGroupOptions}
                  // disabled
                />
                <SelectFormField
                  control={control}
                  isRequired
                  name="role"
                  label="Role"
                  options={roleOptions}
                  // disabled
                />
                <TextInputFormField
                  isRequired
                  label="Title"
                  name="title"
                  control={control}
                />
                <TextInputFormField
                  label="Timezone"
                  name="timezone"
                  control={control}
                />
              </div>
              <div className="flex">
                <TextInputFormField
                  label="Rate"
                  name="rate"
                  control={control}
                  disabled
                />
                <TextInputFormField
                  isRequired
                  label="Cost"
                  name="cost"
                  control={control}
                  type="number"
                />
                <TextInputFormField
                  label="Weekly Hours"
                  name="weeklyHours"
                  control={control}
                  type="number"
                  disabled
                />
              </div>
              <WorkSchedule
                control={control}
                name="workSchedule"
                defaultValue={formData?.workSchedule}
                label="Work Schedule"
              />
            </div>
            <div className="notes">
              <TextInputFormField
                multiline
                rows={17.2}
                label={"User Notes"}
                placeholder={"Type your message..."}
                name={"userNotes"}
                control={control}
              />
            </div>
          </div>
        </UserInfoFormStyled>
      );
    }

    return null;
  };

  return (
    <UserInfoFormStyled>
      <div className="project-cont">
        <div className="row-style">{renderFieldsBasedOnView()}</div>
      </div>
    </UserInfoFormStyled>
  );
};

export default UserInfoForm;
